import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { NetworkStateService } from '../../../core/services/network-state.service';
import { TippyDirective } from '../../directives/tippy.directive';

interface StatusDisplay {
  label: string;
  class: string;
  tooltip: string;
  icon: string;
}

@Component({
  selector: 'app-network-status-badge',
  standalone: true,
  imports: [TippyDirective],
  templateUrl: './network-status-badge.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './network-status-badge.component.css',
})
export class NetworkStatusBadgeComponent {
  private networkState = inject(NetworkStateService);

  readonly connectionState = this.networkState.connectionState;
  readonly stayOfflineMode = this.networkState.stayOfflineMode;
  readonly reconnectAttempts = this.networkState.reconnectAttempts;

  readonly tippyPropsObj = {
    placement: 'bottom' as const,
    theme: 'light-border' as const,
    animation: 'scale' as const,
  };

  readonly statusDisplay = computed<StatusDisplay>(() => {
    const state = this.connectionState();
    const offlineMode = this.stayOfflineMode();
    const attempts = this.reconnectAttempts();

    if (offlineMode) {
      return {
        label: 'Offline Mode',
        class: 'status-offline-mode',
        tooltip: 'Stay offline mode enabled. Manual sync required.',
        icon: '🔒',
      };
    }

    switch (state) {
      case 'online':
        return {
          label: 'Online',
          class: 'status-online',
          tooltip: 'Connected and syncing',
          icon: '🟢',
        };
      case 'connecting':
        return {
          label: attempts > 0 ? `Reconnecting (${attempts})` : 'Connecting',
          class: 'status-connecting',
          tooltip: 'Attempting to reconnect...',
          icon: '🟡',
        };
      case 'offline':
        return {
          label: 'Offline',
          class: 'status-offline',
          tooltip: 'No network connection. Working offline.',
          icon: '🔴',
        };
      default:
        return {
          label: 'Unknown',
          class: 'status-unknown',
          tooltip: 'Network status unknown',
          icon: '⚪',
        };
    }
  });
}
